import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Link as MuiLink,
  Divider
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { SITE_NAME } from '../constants/site';
import { getReleaseNotes } from '../services/api';
import { ReleaseNote } from '../types/ReleaseNote';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ScienceIcon from '@mui/icons-material/Science';
import { useNavigate } from 'react-router-dom';

const CATEGORY_EARLY_ACCESS = 2;

const ReleaseNotesPage: React.FC = () => {
  const [releaseNotes, setReleaseNotes] = useState<ReleaseNote[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchReleaseNotes = async () => {
      try {
        const data = await getReleaseNotes();
        setReleaseNotes(data);
      } catch (error) {
        console.error('リリースノートの取得に失敗しました:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchReleaseNotes();
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}.${m}.${d}`;
  };

  const handleLinkClick = (note: ReleaseNote) => {
    if (note.link_url) {
      if (note.link_url.startsWith('http')) {
        window.open(note.link_url, '_blank', 'noopener noreferrer');
      } else {
        navigate(note.link_url);
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>リリースノート | {SITE_NAME}</title>
      </Helmet>

      <Container maxWidth="md" sx={{ py: 3 }}>
        <Box sx={{ mb: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              borderRadius: '10px',
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)',
            }}
          >
            <AutoAwesomeIcon sx={{ color: '#667eea', fontSize: '1.2rem' }} />
          </Box>
          <Typography variant="h6" component="h1" sx={{ fontWeight: 600 }}>
            リリースノート
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={24} />
          </Box>
        ) : releaseNotes.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>
            リリースノートはまだありません
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {releaseNotes.map((note) => (
              <Card
                key={note.id}
                sx={{
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: note.category === CATEGORY_EARLY_ACCESS
                    ? 'rgba(255, 152, 0, 0.3)'
                    : 'divider',
                  background: note.category === CATEGORY_EARLY_ACCESS
                    ? 'rgba(255, 152, 0, 0.03)'
                    : 'rgba(255, 255, 255, 0.02)',
                  '&:hover': {
                    transform: 'none'
                  }
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, mb: 0.75 }}>
                    {note.title && (
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {note.title}
                      </Typography>
                    )}
                    {note.category === CATEGORY_EARLY_ACCESS && (
                      <Chip
                        icon={<ScienceIcon sx={{ fontSize: '0.75rem' }} />}
                        label="Early Access"
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          backgroundColor: 'rgba(255, 152, 0, 0.15)',
                          color: '#ff9800',
                          border: '1px solid rgba(255, 152, 0, 0.3)',
                          '& .MuiChip-icon': { color: '#ff9800' },
                          '& .MuiChip-label': { px: 1 }
                        }}
                      />
                    )}
                    <Chip
                      label={note.version}
                      size="small"
                      variant="outlined"
                      sx={{
                        height: 18,
                        fontSize: '0.65rem',
                        fontWeight: 500,
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                        color: 'text.secondary',
                        '& .MuiChip-label': { px: 1 }
                      }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.7rem' }}
                    >
                      {formatDate(note.created_at)}
                    </Typography>
                  </Box>

                  <Divider sx={{ mb: 0.75 }} />

                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.75rem',
                      lineHeight: 1.25,
                      whiteSpace: 'pre-wrap',
                      mb: note.link_text ? 0.5 : 0
                    }}
                  >
                    {note.description}
                  </Typography>

                  {note.link_text && (
                    <MuiLink
                      component="button"
                      variant="caption"
                      onClick={() => handleLinkClick(note)}
                      sx={{
                        color: 'text.secondary',
                        textDecoration: 'none',
                        fontWeight: 500,
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      {note.link_text} →
                    </MuiLink>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Container>
    </>
  );
};

export default ReleaseNotesPage;
